import { EMode } from 'constants/contract.constants';
import {
  ContractRefundAttachmentCategory,
  ContractExtensionAttachmentCategory,
  CONTRACT_EXTENSION_STATUS,
} from 'constants/order.constants';
import { REVENUE_EXCEL_TYPE_LABEL } from 'pages/financial/unearnedrevenue/UnearnedRevenueList.constant';
import { AxiosInstance } from 'axios';
import { format } from 'date-fns';
import { TApiResponse, IPagnationParams, ESTATUS } from '@type/common';
import {
  IOrdersRatio,
  IOrderPeriodParam,
  IOrdersReportResponse,
  IOrderRatioDataResponse,
  ITransferOrderParams,
  IGetOrdersByCustomerParams,
  IGetOrdersByCustomerResponse,
  IGetOrderRecordsResponse,
  IOrder,
  ICreateOrderRefundParams,
  IOrderRefundResponse,
  IGetOrderRefundListParams,
  ICreateContractExtensionParams,
  IContractExtensionResponse,
  IGetContractExtensionListResponse,
  IUpdateContractExtensionPayload,
  IAddContractExtensionPayload,
  IPatchOrderRefundParams,
  IUpdateLearningBarOrderByIdPayload,
  IUpdateLearningBarOrderByIdResponse,
  IUpdateTutorialOrderByIdPayload,
  IUpdateTutorialOrderByIdResponse,
  IUpdateSchoolOrderByIdPayload,
  IUpdateSchoolOrderByIdResponse,
  IGetContractsExtensionOrdersParams,
  IGetContractsExtensionOrdersResponse,
  orderState,
  IContractsGroup,
  IPresentCommodity,
  ICreateManualExtensionParams,
  ICreateManualExtensionResponse,
  IGetExtensionRecordResponse,
  IGetExtensionRecordParams,
} from '@type/electroniccommerce/order.type';
import {
  IDeliveryParams,
  IDeliveryResponse,
  IExportDeliveryParams,
  TBatchUpdateDeliveryParams,
} from '@type/textbook.type';
import {
  TDeliveryRecordsResponse,
  TEditCustomerTextbookYearAndSemesterParams,
  TEditCustomerTextbookYearAndSemesterResponse,
  TGetDeliveriesRecordsParams,
  TGetExchangeItemsByOwnerIdParams,
  TGetExchangeItemsByOwnerIdResponse,
  TGetExchangeItemsRecordLogsByIdParams,
  TGetExchangeItemsRecordLogsByIdResponse,
  TUpdateExchangeItemsPackageByOwnerIdParams,
  TUpdateExchangeItemsPackageByOwnerIdResponse,
} from '@type/electroniccommerce/exchange.type';
import {
  TPaymentSettingDto,
  TPaymentSettingResponseDto,
  TMailSetting,
  IPaymentRecordResponse,
  IGetPaymentSettingParams,
} from '@type/electroniccommerce/setting.type';
import { IPlan } from 'pages/electroniccommerce/commodity/plan/types/Plan.type';
import { ISkuAttribute } from '@type/attribute.type';
import {
  IExportUnearnedRevenueListParams,
  IGetRevenuesAmtTotalParams,
  IGetUnearnedRevenueByIdResponse,
  IGetUnearnedRevenueListParams,
  IGetUnearnedRevenueListResponse,
  TGetRevenuesAmtTotalResponse,
} from '@type/revenue';
import {
  EFILE_EXTENSION,
  downloadCsvFile,
  downloadFile,
} from 'utils/downloadFile';
import { ESource } from '@type/electroniccommerce/plan.type';
import {
  IExportPerformanceExpensePayload,
  IGetPerformanceExpensePayload,
  IGetPerformanceExpenseResponse,
} from '@type/financial/performanceExpense.type';
import { fToDate } from 'utils/formatTime';
import { IDepartment } from 'src/@type/department.type';
import { SEMESTER } from 'src/constants/textbook.constants';
import { ContractType } from '@type/electroniccommerce/contract.type';
import { EPayment, EPaymentMethod } from 'features/payment/constants';
import {
  PromotionListDto,
  PromotionDetailDto,
  CreatePromotionParams,
  UpdatePromotionParams,
  PromotionListParams,
} from 'api/product/promotion/type';
import {
  GetSkuListParams,
  SkuResponseStructure,
  SkuParams,
  SkuDto,
} from 'api/product/sku/type';
import {
  GetAttributesListParams,
  AttributesResponseStructure,
  AttributeDto,
  ICreateAttributesParams,
  AttributeTypeDto,
  ICreateAttributesTypesParams,
  ICreateAttributesTypesResponse,
} from 'api/product/attributes/type';
import {
  GetPlanListParams,
  PlanResponseStructure,
  PlanDto,
  CreatePlanParams,
  UpdatePlanParams,
} from 'api/product/plan/type';
import {
  GetSpuListParams,
  SpuResponseStructure,
  SpuDto,
  CreateSpuParams,
  UpdateSpuParams,
} from 'api/product/spu/type';

export interface IAttributesListParams extends IPagnationParams {
  name?: string; // sku名稱
  attributeTypeValue?: string | null; // 屬性類型Id
}

export interface IAttributesListByNamesParams {
  names: string[];
}

export interface IAttributes {
  attributesTypeName: string; // 屬性類型名稱
  createdAt: number; // 建立時間
  id: string; // string
  name: string; // 屬性名稱
  status: ESTATUS; // 屬性啟用與否
  updatedAt: number;
  checked?: boolean;
}

export enum ELECTRONIC_COMMERCE_CATEGORY {
  LIVE_COURSE = 'LiveCourse', // 線上真人課程
  ELECTRONIC = 'electronic', // 3C
  ACTIVE_QUALIFICATION = 'ActiveQualification', // 活動資格
  PAPER_MATERIALS = 'PaperMaterials', // 紙本教材
  INDIVIDUAL_CAMBRIDGE = 'IndividualCambridge', // 劍橋英檢1對1
  HANDLING_FEE = 'HandlingFee', // 服務費
  OTHER = 'Other', // 其他
}

export enum SKU_ATTRIBUTES_GROUP {
  PACKAGE = '配套',
  BONUS = '紅利',
  PURCHASE = '購買',
  REWARD = '回饋',
}

export interface ISkuParams {
  id?: string;
  name?: string; // sku 名稱
  price?: number; // 價格
  category?: ELECTRONIC_COMMERCE_CATEGORY | string; // sku 分類
  attributes?: IAttributesListParams[]; // 屬性
  previewPath?: string; // 預覽
  description?: string; // 簡介
  status?: ESTATUS; // 電商狀態
}

export interface ISku {
  id: string;
  name: string; // sku 名稱
  price: number; // 價格
  type: string; // 課程類型
  category: ELECTRONIC_COMMERCE_CATEGORY; // sku 分類
  attributes: IAttributes[]; // 屬性
  previewPath: string; // 預覽
  description: string; // 簡介
  status: ESTATUS; // 電商狀態
  // quantity?: number
}

export interface ISkuListParams extends IPagnationParams {
  name?: string; // sku名稱
  status?: ESTATUS;
  category?: ELECTRONIC_COMMERCE_CATEGORY[];
  attributesGroup?: SKU_ATTRIBUTES_GROUP[];
}

export interface IPlanQueryParams extends IPagnationParams {
  name?: string; // 搜尋名稱
  status?: string; // 啟用與否

  source?: ESource[]; // 類別 live or 學bar
}

export interface ISkuForProducts {
  sku: ISkuParams;
  quantity: number;
}

export interface IProductsResponse {
  paymentSettings: {
    price: number;
    installment: number;
  }[];
  serialNumber: string;
  name: string;
  price: number;
  status: string;
  promotions?: any[];
  duration: number;
  description: string;
  skus: ISkuForProducts[];
  isExternal: boolean;
  detailHtml: string;
  createdAt: number;
  id: string;
}

export interface IContract {
  mark: string;
  content: string;
}

export interface IContractQueryParams extends IPagnationParams {}

export interface IContractResponse {
  name: string;
  mark: string;
  editor: {
    id: string;
    name: string;
    staffNumber: string;
  };
  content: string;
  createdAt: number;
  id: string;
}
export interface IExtensionContract {
  versionNumber: string;
  mark: string;
  content: string;
}
export interface IGetBusinessContractsQueryParams {
  skip: number;
  limit: number;
}
export interface IPostBusinessContractQueryParams {
  mark: string;
  content: string;
}
export interface IExtensionContractResponse {
  versionNumber: string;
  mark: string;
  editor: {
    id: string;
    name: string;
    staffNumber: string;
  };
  content: string;
  createdAt: number;
  updatedAt: number;

  id: string;
}

export interface IBusinessContractResponse {
  name: string;
  mark: string;
  editor: string;
  content: string;
  updatedAt: number;
}

export interface IBusinessContractHTMLResponse {
  contract: string;
  ver: string;
}

export interface ICreateExtendedOrderPayload {
  contractExtensionId: string;
  order: string;
  status: orderState;
  expired: string;
  month: number;
  contract: string;
  payment: EPayment;
  paymentMethod: EPaymentMethod;
  installment: number;

  commodities: {
    plan: string;
    products: {
      product: string;
      quantity: number;
    }[];
    planPromotions?: string[];
  }[];
  uniformNumbers?: string;
  companyLetterhead?: string;
  companyAddress?: string;
  managers?: string[];
  invoice?: string;
  mailingAddress: {
    county: string;
    districts: string;
    address: string;
  };
  email: string;
}

export interface ICreateExtendedOrderResponse {
  commodities: {
    plan: string;
    products: {
      product: string;
      quantity: number;
    }[];
    planPromotions?: string[];
  }[];
  companyAddress: string;
  companyLetterhead: string;
  contract: string;
  contractExtensionId: string;
  createdAt: string;
  expired: string;
  id: string;
  installment: number;
  invoice: string;
  isSendContract: boolean;
  managers: string[];
  month: number;
  order: string;
  payAmount: string;
  payment: EPayment;
  paymentMethod: EPaymentMethod;
  presentCommodities: {
    plan: {
      name: string;
      id: string;
      description: string;
    };
    products: {
      product: {
        name: string;
        id: string;
        description: string;
        price: number;
        sku: {
          name: string;
          id: string;
          description: string;
          price: number;
        }[];
      };
      quantity: number;
    }[];
  }[];
  status: orderState;
  uniformNumbers: string;
  updatedAt: string;
}

export interface IUpdateExtendedOrderPayload {
  uniformNumbers?: string;
  companyLetterhead?: string;
  companyAddress?: string;
  invoice?: string;

  managers?: string[];
  status?: CONTRACT_EXTENSION_STATUS;
}

export interface TContractEmailVariableResponse {
  name: string;
  value: string;
}

export interface IGetExtendedOrderResponse {
  commodities: {
    plan: string;
    products: {
      product: IProductsResponse;
      quantity: number;
    };
  };
  companyAddress: string;
  companyLetterhead: string;
  contract: string;
  contractExpiredAt: string;
  contractExtensionId: string;
  contractsGroup: IContractsGroup[];
  createdAt: string;
  expired: string;
  id: string;
  installment: number;
  invoice: string;
  isSendContract: boolean;
  managers: {
    departments: IDepartment[];
    name: string;
    staffNumber: string;
    id: string;
  }[];
  month: number;
  order: Pick<
    IOrder,
    | 'amt'
    | 'commodities'
    | 'companyLetterhead'
    | 'creditList'
    | 'customerId'
    | 'customerInfo'
    | 'id'
    | 'payAt'
    | 'productExpired'
    | 'students'
    | 'uniformNumbers'
  >;
  payAmount: string;
  payAt: string;
  payment: EPayment;
  paymentMethod: EPaymentMethod;
  presentCommodities: Pick<IPresentCommodity, 'plan' | 'products'>[];
  status: orderState;
  uniformNumbers: string;
}

export interface IBusinessContract {
  name: string;
  mark: string;
  editor: {
    id: string;
    name: string;
    staffNumber: string;
  };
  content: string;
  type: string;
  createdAt: number;
  id: string;
}

interface IGetBusinessContractsEmailVariableParams {
  type?: ContractType;
}

interface IPerformanceRatio {
  manager: string;
  ratio: number;
}
export interface ISearchOrderListParams {
  skip?: number; // 頁數Offset
  limit?: number; // 顯示筆數
  status?: string; // 訂單狀態
  payment?: string; // 付款方式

  id?: string; // 訂單ID
  crmNo?: string; // CMS交編
  customerId?: string | null; // 客戶ID
  customerName?: string; // 客戶名稱
  // 顧問名稱

  dateType?: string; // 篩選時間類型
  startAt?: number; // 開始時間
  endAt?: number; // 結束時間

  source?: ESource; // 來源
  /** 學年度 */
  schoolYear?: string;
  /** 學期 */
  semester?: SEMESTER.FIRST | SEMESTER.SECOND;
  /** 業績比例 */
  performanceRatio?: IPerformanceRatio[];
}

export interface IPromotionsCanUseParams {
  productId?: string; // 產品ID
  planId?: string; // 方案ID
}

export interface ITransferParams {
  transferMoneyInfo: {
    bankName?: string;
    transferAccount?: string;
    transferDate: number;
  };
  status: string;
}

export interface IGetSkuFindAllResponse {
  activitySku: {
    /** 回饋 */
    freebies: {
      id: string;
      name: string;
    }[];
    /** 紅利 */
    bonus: {
      id: string;
      name: string;
    }[];
    /** 配套 */
    materials: {
      id: string;
      name: string;
    }[];
  };
  sku: ISku;
}
export default (axios: AxiosInstance, electronicCommerce: string) => ({
  /**
   * 批次更新兌換請求
   */
  batchUpdateDelivery(params: TBatchUpdateDeliveryParams): Promise<any> {
    const formData = new FormData();
    formData.append('file', params.file);
    return axios.post(
      `${electronicCommerce}/backend/deliveries/batchUpdate`,
      formData,
      {
        headers: {
          'content-type': 'multipart/form-data',
        },
        responseType: 'blob',
      },
    );
  },

  exportDeliveryRecord(data: IExportDeliveryParams): Promise<any> {
    return axios.get(`${electronicCommerce}/backend/deliveries/exportExcel`, {
      params: data,
      responseType: 'blob',
    });
  },

  getDeliveryList(params: IDeliveryParams): TApiResponse<IDeliveryResponse> {
    return axios.get(`${electronicCommerce}/backend/deliveries`, { params });
  },
  // spu
  /**
   * 建立商品
   */
  createProducts(
    data: CreateSpuParams,
  ): TApiResponse<SpuResponseStructure<SpuDto>> {
    return axios.post(`${electronicCommerce}/products`, data);
  },
  /**
   * 商品列表
   */
  getProductList(
    data: GetSpuListParams,
  ): TApiResponse<SpuResponseStructure<SpuDto>> {
    return axios.get(`${electronicCommerce}/products`, { params: data });
  },
  /**
   * 取得單個商品
   */
  getSpuById(id: string): TApiResponse<SpuDto> {
    return axios.get(`${electronicCommerce}/products/${id}`);
  },
  /**
   * 編輯商品
   */
  updateProductById(id: string, data: UpdateSpuParams): TApiResponse<SpuDto> {
    return axios.patch(`${electronicCommerce}/products/${id}`, data);
  },
  // attributes type
  /**
   * 新增屬性類型選項
   */
  createAttributesTypes(
    data: ICreateAttributesTypesParams,
  ): TApiResponse<ICreateAttributesTypesResponse> {
    return axios.post(`${electronicCommerce}/attributes-types`, data);
  },
  /**
   * 屬性類型列表
   */
  getAttributesTypesList(): TApiResponse<AttributeTypeDto[]> {
    return axios.get(`${electronicCommerce}/attributes-types`);
  },
  // attributes
  /**
   * 新增屬性
   */
  createAttributes(data: ICreateAttributesParams): TApiResponse<AttributeDto> {
    return axios.post(`${electronicCommerce}/attributes`, data);
  },
  /**
   * 屬性列表
   */
  getAteAttributesList(
    data: GetAttributesListParams,
  ): TApiResponse<AttributesResponseStructure<AttributeDto>> {
    return axios.get(`${electronicCommerce}/attributes`, { params: data });
  },
  /**
   * 屬性列表
   */
  getAttributesListByNames(data: IAttributesListByNamesParams): any {
    return axios.get(`${electronicCommerce}/attributes/attributesByNames`, {
      params: data,
    });
  },
  /**
   * 更新屬性
   */
  updateAttributes(
    id: string,
    data: { name: string },
  ): TApiResponse<AttributeDto> {
    return axios.patch(`${electronicCommerce}/attributes/${id}`, data);
  },
  /**
   * 多的名稱取得屬性列表
   */
  getAttributesByNames(data: {
    names: string[];
  }): TApiResponse<ISkuAttribute[]> {
    return axios.get(`${electronicCommerce}/attributes/attributesByNames`, {
      params: data,
    });
  },

  // sku
  /**
   * 建立SKU
   */
  createSku(data: SkuParams): TApiResponse<any> {
    return axios.post(`${electronicCommerce}/sku`, data);
  },
  /**
   * 取得SKU清單
   */
  getSkuList(
    data: GetSkuListParams,
  ): TApiResponse<SkuResponseStructure<SkuDto>> {
    return axios.get(`${electronicCommerce}/sku`, { params: data });
  },
  /**
   * 取得單個SKU
   */
  getSkuById(id: string): TApiResponse<SkuDto> {
    return axios.get(`${electronicCommerce}/sku/${id}`);
  },
  /**
   * 編輯SKU
   */
  updateSkuById(id: string, data: SkuParams): TApiResponse<SkuDto> {
    return axios.patch(`${electronicCommerce}/sku/${id}`, data);
  },
  /**
   * 開關SKU
   */
  switchStatusSkuById(
    id: string,
    data: { status: ESTATUS },
  ): TApiResponse<SkuDto> {
    return axios.patch(`${electronicCommerce}/sku/statusSwitch/${id}`, data);
  },
  /**
   * sku列表 去除屬性：活動
   */
  liveCourseWithoutAttributesActivity(data?: {
    category: string;
  }): TApiResponse<{
    sku: ISku[];
    total: number;
  }> {
    return axios.get(
      `${electronicCommerce}/sku/findAll/liveCourseWithoutAttributesActivity`,
      { params: data },
    );
  },
  /**
   * sku列表-取得該sku以及課程類型為活動的sku
   */
  getSkuFindAll(skuId: string): TApiResponse<IGetSkuFindAllResponse> {
    return axios.get(`${electronicCommerce}/sku/findAll/attributes/${skuId}`);
  },

  // sku end
  // plan
  /**
   * 取得方案列表
   */
  getPlan(
    queryParams?: GetPlanListParams,
  ): TApiResponse<PlanResponseStructure<PlanDto>> {
    return axios.get(`${electronicCommerce}/plan`, {
      params: queryParams,
    });
  },

  getPlanForStaff(
    queryParams?: IPlanQueryParams,
  ): TApiResponse<{ total: number; plans: IPlan[] }> {
    return axios.get(`${electronicCommerce}/plan/forStaff`, {
      params: queryParams,
    });
  },

  getPlanById(id?: string): TApiResponse<PlanDto> {
    return axios.get(`${electronicCommerce}/plan/${id}`);
  },

  createPlan(data: CreatePlanParams): TApiResponse<IPlan> {
    return axios.post(`${electronicCommerce}/plan`, data);
  },

  editPlan(id: string, data: UpdatePlanParams): TApiResponse<PlanDto> {
    return axios.patch(`${electronicCommerce}/plan/${id}`, data);
  },

  // plan end

  // promotion
  /**
   * 取得促銷列表
   */
  getPromotionList(
    params?: PromotionListParams,
  ): TApiResponse<{ total: number; promotions: PromotionListDto[] }> {
    return axios.get(`${electronicCommerce}/promotions`, {
      params,
    });
  },

  getPromotionById(id: string): TApiResponse<PromotionDetailDto> {
    return axios.get(`${electronicCommerce}/promotions/${id}`);
  },

  createPromotion(
    data: CreatePromotionParams,
  ): TApiResponse<PromotionDetailDto> {
    return axios.post(`${electronicCommerce}/promotions`, data);
  },

  updatePromotionById(
    id: string,
    data: UpdatePromotionParams,
  ): TApiResponse<PromotionDetailDto> {
    return axios.patch(`${electronicCommerce}/promotions/${id}`, data);
  },
  /**
   * 取得 單一產品/方案 可使用之促銷列表
   */
  getPromotionsCanUse(data: IPromotionsCanUseParams): TApiResponse<any> {
    return axios.get(`${electronicCommerce}/promotions/can-use`, {
      params: data,
    });
  },

  // promotion end

  // setting contract start
  /**
   * 取得合約列表
   */
  getContracts(
    queryParams?: IContractQueryParams,
  ): TApiResponse<{ total: number; contracts: IContractResponse[] }> {
    return axios.get(`${electronicCommerce}/contracts/template`, {
      params: queryParams,
    });
  },
  /**
   * 修訂合約
   * @param data
   */
  amendmentContract(data: IContract): TApiResponse<IContractResponse> {
    return axios.post(`${electronicCommerce}/contracts/template`, data);
  },
  /**
   * 獲取最新合約
   */
  getLatestContract(): TApiResponse<IContractResponse> {
    return axios.get(`${electronicCommerce}/contracts/template/latest`);
  },
  /**
   * 取得完整合約內容
   */
  getContractById(id: string): TApiResponse<any> {
    return axios.get(`${electronicCommerce}/contracts/${id}`);
  },
  /**
   * 創建PDF & 寄信
   */
  getSendContractAndCreatePdf(data: any): TApiResponse<any> {
    return axios.post(`${electronicCommerce}/contracts/send`, data);
  },
  // contract end

  // setting mail start
  /**
   * 取得信箱列表
   */
  getMailSetting(): any {
    return axios.get(`${electronicCommerce}/email-settings/latest`);
  },
  /**
   * 取得信箱列表
   */
  updateMailSetting(id: string, data: TMailSetting): any {
    return axios.patch(`${electronicCommerce}/email-settings/${id}`, data);
  },
  /**
   * 獲取合約 email 變數列表
   */
  getContractEmailVariable(): TApiResponse<TContractEmailVariableResponse[]> {
    return axios.get(`${electronicCommerce}/email-settings/emailVariables`);
  },

  /**
   * 獲取展延合約 email 變數列表
   */
  getExtensionContractEmailVariable(): TApiResponse<
    TContractEmailVariableResponse[]
  > {
    return axios.get(
      `${electronicCommerce}/email-settings/contracts-extension/emailVariables`,
    );
  },
  // setting paymentFlow start
  /**
   * 取得金流設定
   */
  getPaymentFlowSetting(): TApiResponse<TPaymentSettingResponseDto> {
    return axios.get(`${electronicCommerce}/payment-settings/latest`);
  },
  /**
   * 取得金流異動紀錄
   */
  getPaymentRecord(
    params: IGetPaymentSettingParams,
  ): TApiResponse<IPaymentRecordResponse> {
    return axios.get(`${electronicCommerce}/backend/payment-settings-logs`, {
      params,
    });
  },

  /**
   * 更新金流設定
   */
  updatePaymentFlowSetting(
    id: string,
    data: TPaymentSettingDto,
  ): TApiResponse<TPaymentSettingResponseDto> {
    return axios.patch(`${electronicCommerce}/payment-settings/${id}`, data);
  },
  /**
   * 取得展延合約列表
   */
  getExtensionContracts(queryParams?: IContractQueryParams): TApiResponse<{
    total: number;
    contractExtensionSettings: IExtensionContractResponse[];
  }> {
    return axios.get(
      `${electronicCommerce}/backend/contract-extension-settings`,
      {
        params: queryParams,
      },
    );
  },
  /**
   * 修訂展延合約
   * @param data
   */
  amendmentExtensionContract(
    data: IExtensionContract,
  ): TApiResponse<IExtensionContractResponse> {
    return axios.post(
      `${electronicCommerce}/backend/contract-extension-settings`,
      data,
    );
  },
  /**
   * 獲取最新展延合約
   */
  getLatestExtensionContract(): TApiResponse<IExtensionContractResponse> {
    return axios.get(
      `${electronicCommerce}/backend/contract-extension-settings/latest`,
    );
  },

  /**
   * 獲取單一展延合約
   */
  getExtensionContractById(id: string): TApiResponse<string> {
    return axios.get(
      `${electronicCommerce}/backend/contracts-extension/${id}/contract`,
    );
  },

  // 寄送展延合約
  getSendExtensionContractAndCreatePdf(data: any): TApiResponse<any> {
    return axios.post(
      `${electronicCommerce}/backend/contract-extension-settings/send`,
      data,
    );
  },

  // 手動展延
  createManualExtension(
    id: string,
    data: ICreateManualExtensionParams,
  ): TApiResponse<ICreateManualExtensionResponse> {
    return axios.patch(`${electronicCommerce}/orders/${id}/extension`, data);
  },

  /**
   * 取得補教合約列表
   */
  getBusinessContracts(
    contractType: ContractType,
    queryParams: IGetBusinessContractsQueryParams,
  ): TApiResponse<{
    total: number;
    contracts: IBusinessContract[];
  }> {
    return axios.get(
      `${electronicCommerce}/contracts-business/${contractType}/template`,
      {
        params: queryParams,
      },
    );
  },

  /**
   * 獲取最新補教合約
   */
  getLatestBusinessContract(
    contractType: ContractType,
  ): TApiResponse<IBusinessContractResponse> {
    return axios.get(
      `${electronicCommerce}/contracts-business/${contractType}/template/latest`,
    );
  },

  /**
   * 修訂補教合約
   * @param data
   */
  amendmentBusinessContract(
    contractType: ContractType,
    body: IPostBusinessContractQueryParams,
  ): TApiResponse<IBusinessContractResponse> {
    return axios.post(
      `${electronicCommerce}/contracts-business/${contractType}/template`,
      body,
    );
  },

  /**
   * 獲取單一補教合約
   */
  getBusinessContractById(
    orderId: string,
    contractType: ContractType,
  ): TApiResponse<IBusinessContractHTMLResponse> {
    return axios.get(
      `${electronicCommerce}/contracts-business/${contractType}/${orderId}`,
    );
  },

  // 寄送補教合約
  getSendBusinessContractsAndCreatePdf(
    contractType: ContractType,
    body: {
      orderId: string | undefined;
      emails: string[];
      mode: EMode;
    },
  ): TApiResponse<any> {
    return axios.post(
      `${electronicCommerce}/contracts-business/${contractType}/send`,
      body,
    );
  },

  /**
   * 獲取補教合約 email 變數列表
   */
  getBusinessContractEmailVariable(
    data: IGetBusinessContractsEmailVariableParams,
  ): TApiResponse<TContractEmailVariableResponse[]> {
    return axios.get(
      `${electronicCommerce}/email-settings/business-contracts/emailVariables`,
      {
        params: data,
      },
    );
  },

  // order
  /**
   * 建立 訂單列表
   */
  createOrders(data: ISearchOrderListParams): any {
    return axios.post(`${electronicCommerce}/orders`, {
      ...data,
      from: 'mms',
    });
  },
  /**
   * 取得 訂單列表
   */
  getOrders(data: any): any {
    return axios.get(`${electronicCommerce}/orders`, {
      params: data,
    });
  },
  /**
   * 取得 訂單列表報告
   */
  getOrdersReport(
    data: IOrderPeriodParam,
  ): TApiResponse<IOrdersReportResponse> {
    return axios.get(`${electronicCommerce}/orders/report`, { params: data });
  },
  /**
   * 取得 單一 訂單（新）
   */
  getOrdersById(id: string): TApiResponse<IOrder> {
    return axios.get(`${electronicCommerce}/orders/${id}`);
  },
  /**
   * 取得 單一 訂單（舊）
   */
  getOnePayOrdersById(id: string): any {
    return axios.get(`${electronicCommerce}/onePayOrder/${id}`);
  },
  /**
   * 編輯訂單
   */
  updateOrdersById(id: string, data: any): any {
    return axios.patch(`${electronicCommerce}/orders/${id}`, data);
  },
  /**
   * 編輯訂單 - 學霸
   */
  updateLearningBarOrderById(
    id: string,
    data: IUpdateLearningBarOrderByIdPayload,
  ): TApiResponse<IUpdateLearningBarOrderByIdResponse> {
    return axios.patch(`${electronicCommerce}/orders/learningBar/${id}`, data);
  },
  /**
   * 編輯訂單 - 補習班
   */
  updateTutorialOrderById(
    id: string,
    data: IUpdateTutorialOrderByIdPayload,
  ): TApiResponse<IUpdateTutorialOrderByIdResponse> {
    return axios.patch(`${electronicCommerce}/orders/tutorial/${id}`, data);
  },
  /**
   * 編輯訂單 - 學校
   */
  updateSchoolOrderById(
    id: string,
    data: IUpdateSchoolOrderByIdPayload,
  ): TApiResponse<IUpdateSchoolOrderByIdResponse> {
    return axios.patch(`${electronicCommerce}/orders/school/${id}`, data);
  },
  /**
   * 轉移訂單
   */
  transferOrder(id: string, data: ITransferOrderParams): TApiResponse<any> {
    return axios.post(`${electronicCommerce}/orders/${id}/transfer`, data);
  },

  /**
   * 編輯訂單 轉帳資料
   */
  updateTransferPaymentInfoById(
    id: string,
    data: ITransferParams,
  ): TApiResponse<any> {
    return axios.patch(
      `${electronicCommerce}/intermediate-payment/transfer/${id}`,
      data,
    );
  },
  /**
   * 取消訂單
   */
  cancelOrdersById(id: string): any {
    return axios.post(`${electronicCommerce}/orders/${id}/cancel`);
  },
  /**
   * 匯出訂單 excel
   */
  async exportOrder(data: any): Promise<any> {
    const link = document.createElement('a');

    const excel = await axios.get(`${electronicCommerce}/orders/exportExcel`, {
      params: data,
      responseType: 'blob',
    });

    if (!excel) return false;

    const blob = new Blob([excel as unknown as BlobPart]);
    link.style.display = 'none';
    link.href = URL.createObjectURL(blob);
    link.download = `order_${format(new Date(), 'yyyyMMdd')}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return true;
  },
  /**
   * 下載合約
   */
  async downloadContract(id: string): Promise<any> {
    const link = document.createElement('a');
    link.style.display = 'none';
    link.href = `${electronicCommerce}/orders/${id}/contract`;
    link.download = `${id}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return true;
  },
  /**
   * 展延下載合約
   */
  async downloadContractExtension(id: string): Promise<any> {
    const link = document.createElement('a');
    const contractExtension = await axios.get(
      `${electronicCommerce}/backend/contracts-extension/${id}/download/contract`,
      {
        responseType: 'blob',
      },
    );
    if (!contractExtension) {
      return false;
    }
    const blob = new Blob([contractExtension as unknown as BlobPart]);
    link.style.display = 'none';
    link.href = URL.createObjectURL(blob);
    link.download = `${id}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return true;
  },

  // orderRecords
  getOrderRecords(orderId: string): TApiResponse<IGetOrderRecordsResponse[]> {
    return axios.get(`${electronicCommerce}/orderRecords/${orderId}`);
  },

  // performance-ratio
  /**
   * 取得 單一訂單 業績配比
   */
  getOrdersRatioById(id: string): TApiResponse<IOrdersRatio> {
    return axios.get(`${electronicCommerce}/performance-ratio/info/${id}`);
  },
  /**
   * 新增 業績配比
   */
  updateOrdersRatio(id: string, data: any): TApiResponse<IOrdersRatio> {
    return axios.patch(`${electronicCommerce}/performance-ratio/${id}`, data);
  },

  // services
  /**
   * 檢視oneClubID 是否存在
   */
  getCheckOneClubID(id: string): any {
    return axios.get(`${electronicCommerce}/services/${id}`);
  },

  // intermediate-payment
  /**
   * 查詢金流訂單詳細
   */
  getIntermediatePaymentById(
    id: string,
    data: { type: string; amount: number; paymentMethod?: string },
  ): any {
    return axios.post(`${electronicCommerce}/intermediate-payment/${id}`, data);
  },

  /**
   * 建立 展延訂單
   */
  createExtendedOrders(
    data: ICreateExtendedOrderPayload,
  ): TApiResponse<ICreateExtendedOrderResponse> {
    return axios.post(`${electronicCommerce}/backend/contracts-extension`, {
      ...data,
    });
  },
  updateExtendedOrder(
    id: string,
    data: IUpdateExtendedOrderPayload,
  ): TApiResponse<any> {
    return axios.patch(
      `${electronicCommerce}/backend/contracts-extension/${id}`,
      data,
    );
  },
  /**
   * 取得 展延訂單 by id
   */
  getExtendedOrderById(id: string): any {
    return axios.get(`${electronicCommerce}/backend/contracts-extension/${id}`);
  },
  /**
   * 取得 展延訂單 by os order id
   */
  getExtendedOrderByOsId(id: string): TApiResponse<IGetExtendedOrderResponse> {
    return axios.get(
      `${electronicCommerce}/backend/contracts-extension/${id}/contracts-extensionId`,
    );
  },
  /**
   * 取得 展延訂單 紀錄
   */
  getExtendedOrderRecords(id: string): any {
    return axios.get(
      `${electronicCommerce}/backend/contract-extension-records/${id}`,
    );
  },
  /**
   * 註冊 FCM KEY
   */
  postSetFCMKey(token: any) {
    return axios.post(`${electronicCommerce}/notify/subscribeTopic`, {
      topic: 'deal',
      token,
    });
  },
  /**
   * 取得多筆訂單業務業績分配
   */
  getPerformanceRatioInfos(
    orderIds: string[],
  ): TApiResponse<IOrderRatioDataResponse[]> {
    return axios.post(
      `${electronicCommerce}/performance-ratio/infos`,
      orderIds,
    );
  },
  /**
   * 取得會員下的訂單
   */
  getOrdersByCustomer(
    customerId: string,
    params: IGetOrdersByCustomerParams,
  ): TApiResponse<IGetOrdersByCustomerResponse> {
    return axios.get(
      `${electronicCommerce}/orders/findAllByCustomer/${customerId}`,
      {
        params,
      },
    );
  },

  /**
   * 取得展延訂單 (棄用)
   * 請使用 getContractsExtensionOrders 方法
   * /api/product/backend/contracts-extension
   */
  getContractsExtensionOrdersList(
    params: IGetContractsExtensionOrdersParams,
  ): TApiResponse<IGetContractsExtensionOrdersResponse> {
    return axios.get(`${electronicCommerce}/backend/contracts-extension`, {
      params,
    });
  },
  /**
   * 取得異動紀錄
   */
  getExtensionRecord(
    orderId: string,
    params?: IGetExtensionRecordParams,
  ): TApiResponse<IGetExtensionRecordResponse> {
    return axios.get(
      `${electronicCommerce}/backend/service-extension-records/${orderId}`,
      { params },
    );
  },

  /**
   * 取得預收收入列表
   */
  getUnearnedRevenueList(
    params: IGetUnearnedRevenueListParams,
  ): TApiResponse<IGetUnearnedRevenueListResponse> {
    return axios.get(`${electronicCommerce}/revenues`, { params });
  },

  /**
   * 取得單筆預收收入
   */
  getUnearnedRevenueById(
    id: string,
  ): TApiResponse<IGetUnearnedRevenueByIdResponse> {
    return axios.get(`${electronicCommerce}/revenues/${id}`);
  },
  /**
   * 取得總收入（預收/暫收/認列）
   */
  getRevenuesAmtTotal(
    params: IGetRevenuesAmtTotalParams,
  ): TApiResponse<TGetRevenuesAmtTotalResponse> {
    return axios.get(`${electronicCommerce}/revenues/amtTotal`, { params });
  },
  /**
   * 匯出預收帳款表/暫收帳款表
   */
  async exportUnearnedRevenueList(
    params: IExportUnearnedRevenueListParams,
  ): Promise<boolean> {
    const excel = await axios.post(
      `${electronicCommerce}/revenues/generate/excel`,
      undefined,
      {
        params,
        responseType: 'blob',
      },
    );

    if (excel instanceof Blob) {
      const dateTimeStartAt = format(params.startAt, 'yyyyMMdd');
      const dateTimeEndAt = format(params.endAt, 'yyyyMMdd');
      const typeName = REVENUE_EXCEL_TYPE_LABEL[params.type]();
      const blob = new Blob([excel as unknown as BlobPart]);
      const source = URL.createObjectURL(blob);
      downloadFile(
        source,
        `${dateTimeStartAt}-${dateTimeEndAt}-${typeName}.xlsx`,
        EFILE_EXTENSION.XLSX,
      );
      return true;
    } else {
      return false;
    }
  },

  /**
   * 是否還有可兌換講義套數 (最後一筆講義兌換修改紀錄)
   **/
  getExchangeItemsByOwnerId(
    params: TGetExchangeItemsByOwnerIdParams,
  ): TApiResponse<TGetExchangeItemsByOwnerIdResponse> {
    return axios.get(`${electronicCommerce}/backend/exchange-items-records`, {
      params,
    });
  },

  /**
   * 取得套數兌換異動紀錄
   */
  getExchangeItemsRecordLogsByRecordId(
    params: TGetExchangeItemsRecordLogsByIdParams,
  ): TApiResponse<TGetExchangeItemsRecordLogsByIdResponse> {
    return axios.get(
      `${electronicCommerce}/backend/exchange-items-record-logs`,
      {
        params,
      },
    );
  },

  /**
   * 變更講義兌換數量 by OwnerId (OneClubId)
   * @param id
   * @param params
   */
  updateExchangeItemsPackageByOwnerId(
    ownerId: string,
    params: TUpdateExchangeItemsPackageByOwnerIdParams,
  ): TApiResponse<TUpdateExchangeItemsPackageByOwnerIdResponse> {
    return axios.post(
      `${electronicCommerce}/backend/exchange-items-records/change/owners/${ownerId}`,
      params,
    );
  },

  /**
   * 取得物流單異動紀錄
   */
  getDeliveriesRecordsById(
    params: TGetDeliveriesRecordsParams,
  ): TApiResponse<TDeliveryRecordsResponse> {
    return axios.get(`${electronicCommerce}/backend/delivery-events`, {
      params,
    });
  },

  /**
   * 後台 -> 會員暫緩領取講義
   */
  editCustomerTextbookYearAndSemester(
    data: TEditCustomerTextbookYearAndSemesterParams,
  ): TApiResponse<TEditCustomerTextbookYearAndSemesterResponse> {
    return axios.post(
      `${electronicCommerce}/backend/exchange-items-records/suspend`,
      data,
    );
  },

  /**
   * 建立退費單
   */
  createOrderRefund(
    data: ICreateOrderRefundParams,
  ): TApiResponse<IOrderRefundResponse> {
    const formData = new FormData();
    const { signedContractFiles, otherAttachmentsFiles, ...restData } = data;
    signedContractFiles.forEach(file => {
      formData.append(
        ContractRefundAttachmentCategory.SIGNATURE_ATTACHMENT_FILES,
        file,
      );
    });
    otherAttachmentsFiles.forEach(file => {
      formData.append(
        ContractRefundAttachmentCategory.OTHER_ATTACHMENT_FILES,
        file,
      );
    });
    Object.entries(restData).forEach(([key, value]) => {
      formData.append(key, value as string);
    });
    return axios.post(`${electronicCommerce}/backend/refund`, formData, {
      headers: {
        'content-type': 'multipart/form-data',
      },
    });
  },

  /**
   * 更新退費單
   */
  updateOrderRefund(
    id: string,
    data: IPatchOrderRefundParams,
  ): TApiResponse<IOrderRefundResponse> {
    const formData = new FormData();
    const {
      signedContractFiles,
      otherAttachmentsFiles,
      updatedSignedContracts,
      updatedOtherAttachments,
      ...restData
    } = data;
    if (signedContractFiles) {
      signedContractFiles.forEach(file => {
        formData.append(
          ContractRefundAttachmentCategory.SIGNATURE_ATTACHMENT_FILES,
          file,
        );
      });
    }
    if (otherAttachmentsFiles) {
      otherAttachmentsFiles.forEach(file => {
        formData.append(
          ContractRefundAttachmentCategory.OTHER_ATTACHMENT_FILES,
          file,
        );
      });
    }
    if (updatedSignedContracts) {
      formData.append(
        'updatedSignedContracts',
        JSON.stringify(updatedSignedContracts),
      );
    }
    if (updatedOtherAttachments) {
      formData.append(
        'updatedOtherAttachments',
        JSON.stringify(updatedOtherAttachments),
      );
    }
    Object.entries(restData).forEach(([key, value]) => {
      formData.append(key, value as string);
    });

    return axios.patch(`${electronicCommerce}/backend/refund/${id}`, formData, {
      headers: {
        'content-type': 'multipart/form-data',
      },
    });
  },

  /**
   * 退費單列表
   */
  getOrderRefundList(
    data: IGetOrderRefundListParams,
  ): TApiResponse<IOrderRefundResponse[]> {
    return axios.get(`${electronicCommerce}/backend/refund`, { params: data });
  },

  /**
   * 取得單筆退費單
   */
  getOrderRefundById(id: string): TApiResponse<IOrderRefundResponse> {
    return axios.get(`${electronicCommerce}/backend/refund/${id}`);
  },

  /**
   * 建立合約展延契約
   */
  createContractExtension(
    data: ICreateContractExtensionParams,
  ): TApiResponse<IContractExtensionResponse> {
    const formData = new FormData();
    const { contractFiles, expired, order } = data;
    contractFiles?.forEach(file => {
      formData.append(ContractExtensionAttachmentCategory.CONTRACT_FILES, file);
    });

    formData.append('expired', expired.toISOString());

    formData.append('order', order);

    return axios.post(
      `${electronicCommerce}/backend/contracts-extension`,
      formData,
      {
        headers: {
          'content-type': 'multipart/form-data',
        },
      },
    );
  },

  /**
   * 取得合約延展列表
   */
  getContractExtensionList(
    id: string,
  ): TApiResponse<IGetContractExtensionListResponse> {
    return axios.get(`${electronicCommerce}/backend/contracts-extension/${id}`);
  },

  /**
   * 更新展延合約
   */
  updateContractExtension(
    id: string,
    data: IUpdateContractExtensionPayload,
  ): TApiResponse<IGetContractExtensionListResponse> {
    return axios.patch(
      `${electronicCommerce}/backend/contracts-extension/${id}`,
      data,
    );
  },

  /**
   * 新增合約
   */
  addContractExtension(
    id: string,
    data: IAddContractExtensionPayload,
  ): TApiResponse<IGetContractExtensionListResponse> {
    const formData = new FormData();

    const { updatedContracts, contractFiles, expired } = data;

    contractFiles?.forEach(file => {
      formData.append(ContractExtensionAttachmentCategory.CONTRACT_FILES, file);
    });

    formData.append('expired', expired?.toISOString());

    formData.append(
      ContractExtensionAttachmentCategory.UPDATED_CONTRACTS,
      JSON.stringify(updatedContracts),
    );

    return axios.patch(
      `${electronicCommerce}/backend/contracts-extension/${id}`,
      formData,
      {
        headers: {
          'content-type': 'multipart/form-data',
        },
      },
    );
  },

  /**
   * 取得業務成本列表
   */
  getPerformanceExpenseList(
    data: IGetPerformanceExpensePayload,
  ): TApiResponse<IGetPerformanceExpenseResponse> {
    return axios.get(`${electronicCommerce}/backend/performance-expense`, {
      params: data,
    });
  },

  /**
   * 匯出業務成本表
   */
  async exportPerformanceExpenseExcel(
    params: IExportPerformanceExpensePayload,
  ): Promise<any> {
    if (!params.startAt || !params.endAt) return false;

    const excel: Blob = await axios.get(
      `${electronicCommerce}/backend/performance-expense/export-excel`,
      {
        params,
        responseType: 'blob',
      },
    );

    const typeCount = {
      type: 'application/csv',
    };
    const blob = new Blob([excel as unknown as BlobPart], typeCount);
    const fileName = `${fToDate(params.startAt)}~${fToDate(
      params.endAt,
    )}業務成本表`;

    if (excel.type !== typeCount.type) {
      const fileReader = new FileReader();
      fileReader.readAsText(blob, 'UTF-8');

      fileReader.onload = function () {
        const error = JSON.parse(fileReader.result as string).error;
        if (error) {
          return false;
        }
      };
    } else {
      downloadCsvFile(fileName, blob);
      return true;
    }
  },

  /**
   * 匯入業務成本表
   */
  async importPerformanceExpenseExcel(file: File): TApiResponse<string> {
    const form = new FormData();
    form.append('file', file);

    return await axios.post(
      `${electronicCommerce}/backend/performance-expense/excel-batch-update`,
      form,
      {
        headers: {
          'content-type': 'multipart/form-data',
        },
      },
    );
  },
});
